import React from 'react';
import { Box, TablePagination, Tooltip } from '@mui/material';
import { DataElement, DictionaryEntry, PAGING_KEYS, groupIdentifierOf } from '../../services/campaignInsightsService';
import { tierMeta } from '../Benchmark/BenchmarkVocabulary';
import { isMarkdownValue, isPrimitiveEntry, safeValue } from './InsightItems';
import { dsVar, ink } from '../../theme/tokens';
import { tableCellPadSx, tableCellTypeSx, tableHeadTypeSx } from '../../theme/dataTable';

/**
 * One page of a capsule's output summary: a row per group, each opening the
 * group's full detail.
 *
 * Columns come from the rows' own entries, since each flow names its fields
 * differently. The AML case fields lead when present, so priority and review
 * status are never pushed off the end by whatever happens to come first.
 */

export interface SummaryColumn {
    key: string;
    label: string;
}

/**
 * Shown in the row heading (the group summary under the name), used to drive the
 * drill-down, or paging bookkeeping — not columns.
 */
const NON_COLUMN_KEYS = new Set([
    'title', 'groupName', 'groupSummary', 'groupIdentifier',
    'userReviewOptions', 'userReviewSessionId', 'userReviewThreadId', 'userReviewAction',
    ...PAGING_KEYS,
]);
const PREFERRED_KEYS = ['alertPriority', 'priorityScore', 'priorityReviewStatus'];
const MAX_COLUMNS = 5;
/** Longer values are prose (a rationale) and belong in the drawer, not a cell. */
const MAX_CELL_TEXT = 80;

const entryKey = (entry: DictionaryEntry): string => entry.key ?? entry.label;

const summaryEntries = (row: DataElement): DictionaryEntry[] =>
    row.items?.find(item => item.objectType === 'DICTIONARY' && item.entries)?.entries ?? [];

const isColumnEntry = (entry: DictionaryEntry): boolean =>
    !NON_COLUMN_KEYS.has(entryKey(entry)) &&
    entry.label.toLowerCase() !== 'title' &&
    isPrimitiveEntry(entry.value) &&
    !isMarkdownValue(entry.value) &&
    String(entry.value).length <= MAX_CELL_TEXT;

export function summaryColumns(rows: DataElement[]): SummaryColumn[] {
    const seen = new Map<string, string>();
    for (const row of rows) {
        for (const entry of summaryEntries(row)) {
            const key = entryKey(entry);
            if (!seen.has(key) && isColumnEntry(entry)) seen.set(key, entry.label);
        }
    }
    const preferred = PREFERRED_KEYS.filter(key => seen.has(key));
    const rest = [...seen.keys()].filter(key => !PREFERRED_KEYS.includes(key));
    return [...preferred, ...rest].slice(0, MAX_COLUMNS).map(key => ({ key, label: seen.get(key) as string }));
}

const hasText = (value: unknown): boolean => value !== undefined && value !== null && value !== '';

/**
 * The row heading: its title, else its own groupName entry. The group's
 * `groupName` is a last resort, as the platform re-cases it for grouping
 * ("Fan-In (ENT-D4)" becomes "Fan- In ( E N T- D4)").
 */
const rowTitle = (row: DataElement): string => {
    const entries = summaryEntries(row);
    const title = entries.find(e => e.label.toLowerCase() === 'title')?.value;
    if (hasText(title)) return String(title);
    const groupName = entries.find(e => e.key === 'groupName')?.value;
    return hasText(groupName) ? String(groupName) : row.groupName;
};

/** The group's one-line summary, shown under its heading; prose, so never a column. */
const rowSummary = (row: DataElement): string | null => {
    const summary = summaryEntries(row).find(e => e.key === 'groupSummary')?.value;
    return hasText(summary) && typeof summary !== 'object' ? String(summary) : null;
};

const pillSx = (color: string) => ({
    display: 'inline-block', px: 1, py: 0.3, borderRadius: '999px', fontSize: '0.6875rem', fontWeight: 700,
    whiteSpace: 'nowrap', color: ink(color), backgroundColor: `${color}22`, border: `1px solid ${color}55`,
});

const REVIEW_STATUS_COLORS: Record<string, string> = {
    pending: '#FBBF24',
    approved: '#10B981',
    overridden: '#FFDE59',
};

const CellValue: React.FC<{ columnKey: string; value: unknown }> = ({ columnKey, value }) => {
    if (value === null || value === undefined || value === '') return <span style={{ color: dsVar('fg-muted') }}>—</span>;
    if (columnKey === 'alertPriority') {
        return <Box component="span" sx={pillSx(tierMeta(String(value)).color)}>{String(value)}</Box>;
    }
    if (columnKey === 'priorityReviewStatus') {
        const status = String(value);
        return (
            <Box component="span" sx={{ ...pillSx(REVIEW_STATUS_COLORS[status.toLowerCase()] ?? '#94A3B8'), textTransform: 'capitalize' }}>
                {status}
            </Box>
        );
    }
    return <>{safeValue(value)}</>;
};

const headSx = {
    ...tableHeadTypeSx, ...tableCellPadSx, textAlign: 'left' as const, whiteSpace: 'nowrap' as const,
    position: 'sticky' as const, top: 0, backgroundColor: dsVar('raised'), zIndex: 1,
};
const cellSx = { ...tableCellTypeSx, ...tableCellPadSx, verticalAlign: 'middle' };

export const PAGE_SIZE_OPTIONS = [10, 25, 50];

interface InsightsSummaryTableProps {
    rows: DataElement[];
    /** Total groups across all pages; null when unknown. */
    total: number | null;
    /** 0-based, as TablePagination counts. */
    page: number;
    pageSize: number;
    /** A later page is loading; the current rows stay visible but inert. */
    loading?: boolean;
    selectedGroupId: string | null;
    onSelect: (row: DataElement, groupIdentifier: string) => void;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
}

const InsightsSummaryTable: React.FC<InsightsSummaryTableProps> = ({
    rows, total, page, pageSize, loading, selectedGroupId, onSelect, onPageChange, onPageSizeChange,
}) => {
    const columns = summaryColumns(rows);
    // Without a reported total, a short page is the last one; a full page may
    // have more after it (-1 renders as "of more than N").
    const count = total ?? (rows.length < pageSize ? page * pageSize + rows.length : -1);

    return (
        <Box className="flex flex-col flex-1 overflow-hidden">
            <Box
                className="flex-1 overflow-auto custom-scrollbar"
                sx={{ opacity: loading ? 0.5 : 1, pointerEvents: loading ? 'none' : 'auto', transition: 'opacity 150ms' }}
                aria-busy={loading || undefined}
            >
                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                    <Box component="thead">
                        <Box component="tr">
                            <Box component="th" sx={headSx}>Group</Box>
                            {columns.map(c => <Box key={c.key} component="th" sx={headSx}>{c.label}</Box>)}
                        </Box>
                    </Box>
                    <Box component="tbody">
                        {rows.map((row, idx) => {
                            const groupId = groupIdentifierOf(row);
                            const selected = Boolean(groupId) && groupId === selectedGroupId;
                            const entries = summaryEntries(row);
                            const summary = rowSummary(row);
                            const open = () => { if (groupId) onSelect(row, groupId); };
                            const tr = (
                                <Box
                                    key={groupId ?? `${row.groupName}-${idx}`}
                                    component="tr"
                                    onClick={open}
                                    onKeyDown={(e: React.KeyboardEvent) => {
                                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); }
                                    }}
                                    tabIndex={groupId ? 0 : -1}
                                    aria-selected={selected}
                                    sx={{
                                        borderTop: `1px solid ${dsVar('line')}`,
                                        cursor: groupId ? 'pointer' : 'default',
                                        backgroundColor: selected ? dsVar('raised') : 'transparent',
                                        boxShadow: selected ? `inset 2px 0 0 ${dsVar('accent-fg')}` : 'none',
                                        '&:hover': groupId ? { backgroundColor: dsVar('raised') } : undefined,
                                        '&:focus-visible': { outline: `1px solid ${dsVar('accent-fg')}`, outlineOffset: -1 },
                                    }}
                                >
                                    <Box component="td" sx={{ ...cellSx, color: dsVar('fg-body'), fontWeight: 600, minWidth: 220 }}>
                                        {rowTitle(row)}
                                        {summary && (
                                            <Box
                                                component="div"
                                                title={summary}
                                                sx={{
                                                    mt: 0.5, fontWeight: 400, fontSize: '0.75rem', lineHeight: 1.45, color: dsVar('fg-2'),
                                                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                                                }}
                                            >
                                                {summary}
                                            </Box>
                                        )}
                                    </Box>
                                    {columns.map(c => {
                                        const entry = entries.find(e => entryKey(e) === c.key);
                                        return (
                                            <Box key={c.key} component="td" sx={cellSx}>
                                                <CellValue columnKey={c.key} value={entry?.value} />
                                            </Box>
                                        );
                                    })}
                                </Box>
                            );
                            return groupId ? tr : (
                                <Tooltip key={`${row.groupName}-${idx}`} title="This group has no identifier, so its detail can't be opened." placement="top-start">
                                    {tr}
                                </Tooltip>
                            );
                        })}
                    </Box>
                </Box>
            </Box>

            <TablePagination
                component="div"
                count={count}
                page={page}
                rowsPerPage={pageSize}
                rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                onPageChange={(_e, next) => onPageChange(next)}
                onRowsPerPageChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
                sx={{
                    color: dsVar('fg-2'),
                    borderTop: `1px solid ${dsVar('line')}`,
                    flexShrink: 0,
                    '& .MuiTablePagination-selectIcon': { color: dsVar('fg-muted') },
                    '& .MuiIconButton-root.Mui-disabled': { color: dsVar('fg-muted', 0.4) },
                    '& .MuiIconButton-root': { color: dsVar('fg-2') },
                }}
            />
        </Box>
    );
};

export default InsightsSummaryTable;
